import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
} from "react";
import { cn } from "../utils";

export type TableProps = ComponentProps<"table"> & {
  /**
   * Bounds the height so `TableHeader` can actually stick. Only worth it on
   * long operational lists — a nested scroll area costs more than it gives on
   * a table that already fits.
   */
  stickyHeader?: boolean;
};

export function Table({ className, stickyHeader, ...props }: TableProps) {
  const scroller = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ start: false, end: false });

  const sync = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setEdges({
      start: el.scrollLeft > 1,
      end: max > 1 && el.scrollLeft < max - 1,
    });
  }, []);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    for (const child of Array.from(el.children)) observer.observe(child);
    return () => observer.disconnect();
  }, [sync]);

  return (
    <div className="relative w-full min-w-0 max-w-full">
      <div
        ref={scroller}
        onScroll={sync}
        className={cn(
          "w-full min-w-0 max-w-full overflow-x-auto",
          stickyHeader && "max-h-[calc(100dvh-16rem)] overflow-y-auto",
        )}
      >
        <table
          className={cn("w-full caption-bottom text-[0.8125rem]", className)}
          {...props}
        />
      </div>
      <ScrollEdge side="start" visible={edges.start} />
      <ScrollEdge side="end" visible={edges.end} />
    </div>
  );
}

/** Tells the user there is more table off-screen. Silent overflow reads as missing data. */
function ScrollEdge({
  side,
  visible,
}: {
  side: "start" | "end";
  visible: boolean;
}) {
  return (
    <div
      aria-hidden
      className={cn(
        "pointer-events-none absolute inset-y-0 w-8 transition-opacity duration-150 ease-swift",
        side === "start"
          ? "left-0 bg-gradient-to-r from-surface-raised to-transparent"
          : "right-0 bg-gradient-to-l from-surface-raised to-transparent",
        visible ? "opacity-100" : "opacity-0",
      )}
    />
  );
}

export function TableHeader({ className, ...props }: ComponentProps<"thead">) {
  return (
    <thead
      className={cn(
        "sticky top-0 z-20 border-b border-line bg-surface-sunken",
        className,
      )}
      {...props}
    />
  );
}

export function TableBody({ className, ...props }: ComponentProps<"tbody">) {
  return <tbody className={cn("divide-y divide-line", className)} {...props} />;
}

export function TableRow({ className, ...props }: ComponentProps<"tr">) {
  return (
    <tr
      className={cn(
        "transition-colors duration-100 hover:bg-surface-sunken/50 data-[state=selected]:bg-ember-subtle",
        className,
      )}
      {...props}
    />
  );
}

export function TableHead({ className, ...props }: ComponentProps<"th">) {
  return (
    <th
      className={cn(
        "h-8 whitespace-nowrap px-3 text-left align-middle text-2xs font-medium uppercase tracking-wider text-ink-muted",
        className,
      )}
      {...props}
    />
  );
}

export function TableCell({ className, ...props }: ComponentProps<"td">) {
  return (
    <td
      className={cn("px-3 py-2 align-middle text-ink-secondary", className)}
      {...props}
    />
  );
}
